import { fetchApi, isApiError } from "@/lib/api-helpers";
import { z } from "zod";
import { agreementSchema } from "@/networking/agreements.api";
import { returnedPaymentMethodSchema } from "@/networking/payments.api";
import { shippingOptionSchema } from "@/networking/shipping.api";

export const cartCountrySchema = z.object({
  id: z.number(),
  iso: z.string(),
  name: z.string(),
  province_required: z.boolean(),
  price_inclusive_of_tax: z.boolean().optional(),
  price_inclusive_tax_name: z.string().optional(),
});

const subscriptionPlansSchema = z.object({
  id: z.number(),
  allow_max_billing_cycles: z.boolean().optional(),
  billing_frequency_in_words: z.string().optional(),
  billing_interval: z.number(),
  billing_interval_unit: z.string(),
  company_default: z.boolean().optional(),
  max_billing_cycles: z.number().nullable().optional(),
  name: z.string().optional(),
  price: z.string().optional(),
  price_in_currency: z.string().optional(),
  products_count: z.number().optional(),
  shipping_interval: z.number().optional(),
  shipping_interval_unit: z.string().optional(),
  split_volume: z.boolean().optional(),
  subscribe_and_save: z.string(),
  subscribe_and_save_for_display: z.string().optional(),
  subscribe_and_save_in_currency: z.string().optional(),
  subscribers_count: z.number().optional(),
  subscription_start: z.string().optional(),
  trial_period: z.number().optional().nullable(),
  trial_period_unit: z.string().optional().nullable(),
  volume_interval: z.number().optional().nullable(),
  volume_interval_unit: z.string().optional().nullable(),
});

export type SubscriptionPlan = z.infer<typeof subscriptionPlansSchema>;

export const variantOptionSchema = z.object({
  title: z.string(),
  value: z.string(),
});

export type VariantOption = z.infer<typeof variantOptionSchema>;

export const baseProductSchema = z.object({
  id: z.number(),
  cv: z.number(),
  image_path: z.string().nullable(),
  image_url: z.string().nullable(),
  introduction: z.string().nullable(),
  price: z.string(),
  price_in_currency: z.string(),
  sku: z.string().nullable(),
  tax: z.string(),
  tax_in_currency: z.string(),
  title: z.string(),
});

export type BaseProduct = z.infer<typeof baseProductSchema>;

const variantSchema = z.object({
  id: z.number(),
  image_path: z.string().nullable(),
  image_url: z.string().nullable(),
  primary_image: z.string().nullable(),
  price: z.string(),
  price_in_currency: z.string(),
  title: z.string(),
  sku: z.string().nullable(),
  options: z.array(variantOptionSchema),
});

export type Variant = z.infer<typeof variantSchema>;

export const bundledItemSchema = z.object({
  quantity: z.number(),
  product_id: z.number(),
  variant_id: z.number(),
  option_titles: z.array(z.string()),
  product_title: z.string(),
  variant_title: z.string(),
  display_to_customer: z.boolean(),
});

export type BundledItem = z.infer<typeof bundledItemSchema>;

export const cartItemSchema = z.object({
  id: z.number(),
  title: z.string().nullable().optional(),
  bundled_items: z.array(bundledItemSchema).optional(),
  allow_subscription: z.boolean(),
  cv: z.number(),
  discount_amount: z.string().optional(),
  image_url: z.string().nullable(),
  price: z.string(),
  price_in_currency: z.string(),
  display_to_customer: z.boolean().optional().default(true),
  display_externally: z.boolean().optional().default(true),
  enrollment: z.boolean(),
  enrollment_pack_id: z.number().nullable(),
  parent_id: z.number().nullable().optional(),
  free_item: z.boolean().optional().default(false),
  product: baseProductSchema
    .extend({
      image_url: z.string().nullable(),
    })
    .nullable(),
  product_title: z.string().nullable(),
  quantity: z.number(),
  qv: z.number(),
  subscribe_and_save: z.coerce.string(),
  subscribe_and_save_for_display: z.string().nullable(),
  subscribe_and_save_in_currency: z.string().nullable(),
  subscription: z.boolean(),
  subscription_interval: z.number().nullable(),
  subscription_interval_unit: z.string().nullable(),
  subscription_only: z.boolean().nullable(),
  subscription_price: z.string().nullable().optional(),
  subscription_price_in_currency: z.string().nullable(),
  subscription_start: z.string().nullable(),
  subscription_plans: z.array(subscriptionPlansSchema),
  subscription_plan_id: z.number().nullable(),
  tax: z.string().optional(),
  tax_in_currency: z.string().optional(),
  variant: variantSchema.nullable(),
  errors: z
    .array(z.record(z.array(z.string())))
    .nullable()
    .optional(),
});

export type CartItem = z.infer<typeof cartItemSchema>;

export const enrollmentItemSchema = z.object({
  ...cartItemSchema.shape,
  product: z.object({}).optional(),
  variant: variantSchema.optional(),
});

export type EnrollmentItem = z.infer<typeof enrollmentItemSchema>;

const enrollmentPackSchema = z.object({
  enrollment_pack_id: z.number(),
  items: z.array(enrollmentItemSchema),
  price: z.string(),
  price_in_currency: z.string(),
  subscription_price_in_currency: z.string(),
  subscription_price: z.string(),
  title: z.string(),
  images: z.array(
    z.object({
      image_url: z.string(),
    }),
  ),
});

export type EnrollmentPack = z.infer<typeof enrollmentPackSchema>;

const recurringSchema = z.object({
  subtotal: z.string(),
  subtotal_in_currency: z.string(),
  interval: z.number(),
  interval_unit: z.string(),
  subtotal_for_display: z.string(),
  start_date: z.string().nullable(),
});

const customerDetailsFieldSchema = z.object({
  label: z.string(),
  type: z.string(),
  required: z.boolean().optional(),
});

const customerDetailsSchema = z.record(customerDetailsFieldSchema).optional();

const availablePaymentMethodSchema = z.object({
  id: z.number(),
  type: z.string(),
  name: z.string(),
  public_api_key: z.string().optional(),
  publishable_key: z.string().nullable().optional(),
  supported_brands: z.array(z.string()).optional(),
  test: z.boolean(),
  sandbox: z.boolean().optional(),
  client_id: z.string().optional(),
  merchant_id: z.string().nullish(),
  apple_pay: z.boolean().optional(),
  apple_pay_merchant_identifier: z.string().nullable().optional(),
  google_pay: z.boolean().optional(),
  image_url: z.string().nullable().optional(),
  tokenize_path: z.string().nullable().optional(),
  token_provider: z.string().optional(),
  customer_details_schema: customerDetailsSchema,
  collect_billing_address: z.boolean().optional(),
  script: z.string().optional(),
  sdk_type: z.string().optional(),
  braintree_payment_account_id: z.string().optional(),
});

export type AvailablePaymentMethod = z.infer<
  typeof availablePaymentMethodSchema
>;

export const cartResponseSchema = z.object({
  cart: z.object({
    id: z.number(),
    agreements: z.array(agreementSchema),
    amount_total: z.string(),
    amount_total_in_currency: z.string(),
    attribution: z
      .object({
        id: z.number(),
        share_guid: z.string().optional(),
      })
      .nullable(),
    available_payment_methods: z.array(availablePaymentMethodSchema),
    available_shipping_methods: z.array(shippingOptionSchema),
    bill_to: z
      .object({
        id: z.number(),
        address1: z.string().nullable(),
        address2: z.string().nullable(),
        address3: z.string().nullable(),
        city: z.string().nullable(),
        country_code: z.string().nullable(),
        default: z.boolean().nullable(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        name: z.string().nullable(),
        postal_code: z.string().nullable(),
        state: z.string().nullable(),
        subdivision_code: z.string().nullable(),
      })
      .nullable(),
    buyer_rep_id: z.number().nullable(),
    buyer_rep: z
      .object({
        id: z.number(),
        email: z.string(),
        full_name: z.string(),
        image_url: z.string().nullable(),
      })
      .nullable(),
    cart_token: z.string(),
    company: z.object({
      id: z.number(),
      checkout_primary_button_color: z.string().nullable(),
      checkout_primary_text_color: z.string().nullable(),
      checkout_secondary_button_color: z.string().nullable(),
      checkout_secondary_text_color: z.string().nullable(),
      collect_email_marketing: z.boolean().nullable(),
      collect_phone: z.boolean(),
      collect_sms_marketing: z.boolean().nullable(),
      favicon_url: z.string().nullable(),
      fluid_shop: z.string().nullable(),
      logo_url: z.string().nullable(),
      name: z.string(),
      order_on_behalf: z.boolean(),
      primary_domain_hostname: z.string().nullable(),
      require_phone: z.boolean(),
      subdomain: z.string(),
      reward_points_enabled: z.boolean().optional().default(false),
    }),
    country: cartCountrySchema,
    currency_code: z.string(),
    currency_decimal_places: z.number().optional().default(2),
    currency_symbol: z.string(),
    cv_total: z.number(),
    digital_only: z.boolean(),
    discount_breakdown: z.object({
      internal_discounts: z.number(),
      external_discounts: z.number(),
      total_discounts: z.number(),
    }),
    product_discount_codes: z.array(z.string()),
    shipping_discount_codes: z.array(z.string()),
    discount_total: z.string(),
    discount_total_in_currency: z.string(),
    email: z.string().nullable(),
    email_marketing: z.boolean().nullable().default(true),
    enrollment_fee: z.coerce.number(),
    enrollment_fee_in_currency: z.string(),
    enrollment_pack: enrollmentPackSchema.nullable(),
    enrollment_pack_id: z.number().nullable(),
    immutable_items: z.boolean(),
    items: z.array(cartItemSchema),
    language_iso: z.string().nullable(),
    payment_method: returnedPaymentMethodSchema.nullable(),
    payment_account: z.string().nullish(),
    payment_method_auto_assignable: z.boolean(),
    phone: z.string().nullable(),
    processed: z.boolean(),
    qv_total: z.number(),
    recurring: z.array(recurringSchema),
    ship_to: z
      .object({
        id: z.number(),
        address1: z.string(),
        address2: z.string().nullable(),
        address3: z.string().nullable(),
        city: z.string(),
        country_code: z.string().nullable(),
        default: z.boolean(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        name: z.string(),
        postal_code: z.string(),
        state: z.string(),
        subdivision_code: z.string().nullable(),
      })
      .nullable(),
    shipping_discount: z.number(),
    shipping_method: z
      .object({
        id: z.string().nullable().default(""),
        name: z.string().nullable().default(""),
      })
      .nullable(),
    shipping_total: z.string().nullable(),
    shipping_total_for_display: z.string().nullable(),
    shipping_total_in_currency: z.string(),
    sms_marketing: z.boolean().nullable().default(true),
    state: z.string(),
    sub_total: z.string(),
    sub_total_in_currency: z.string(),
    subtotal_after_discounts: z.string(),
    subtotal_after_discounts_in_currency: z.string(),
    tax_total: z.string(),
    tax_total_in_currency: z.string(),
    total_creditable_points: z.number().int().optional(),
    transaction_fee: z.coerce.number(),
    transaction_fee_in_currency: z.string(),
    type: z.string(),
    valid_for_checkout: z.boolean(),
    requires_3ds: z.boolean().optional().default(false),
    three_ds_provider: z.enum(["vgs"]).optional(),
    three_ds_challenge_html: z.string().optional(),
    three_ds_transaction_id: z.string().optional(),
    vgs_card_id: z.string().optional(),
    volume_rep: z
      .object({
        id: z.number(),
        email: z.string(),
        full_name: z.string(),
        image_url: z.string().nullable(),
      })
      .nullable(),
    messages: z
      .object({
        errors: z.array(z.string()).default([]),
      })
      .default({ errors: [] }),

    points_enabled: z.boolean().optional(),
    customer_total_points: z.number().int().optional(),
    customer_total_points_in_currency: z.number().optional(),
    points_applied: z.number().int().optional().default(0),
    points_applied_amount_in_currency: z.number().optional(),
    remaining_customer_points: z.number().int().optional(),
    remaining_customer_points_amount_in_currency: z.number().optional(),
    remaining_cart_amount_after_points_in_currency: z.string().optional(),
    max_applicable_points: z.number().int().optional(),
  }),
  meta: z.object({
    shop_url: z.string().optional(),
    request_id: z.string(),
    timestamp: z.string(),
    checkout_url: z.string().optional(),
  }),
});

export type CartResponse = z.infer<typeof cartResponseSchema>;

export async function fetchCart(
  cartToken: string,
  paymentMethodsIncludes?: string,
) {
  try {
    let endpoint = `/carts/${cartToken}`;

    if (paymentMethodsIncludes) {
      endpoint += `?payment_methods_includes=${paymentMethodsIncludes}`;
    }

    return await fetchApi({
      endpoint,
      method: "GET",
      outputSchema: cartResponseSchema,
      fetchOptions: {
        priority: "high",
      },
    });
  } catch (error) {
    if (isApiError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function recalculateCart(cartToken: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/recalculate`,
    method: "POST",
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartAttributes({
  cartToken,
  email,
  phone,
}: {
  cartToken: string;
  email?: string;
  phone?: string;
}) {
  const payload: Record<string, string> = {};

  if (email !== undefined) {
    payload.email = email;
  }

  if (phone !== undefined) {
    payload.phone = phone;
  }

  return fetchApi({
    endpoint: `/carts/${cartToken}`,
    method: "PATCH",
    input: payload,
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartPhone({
  cartToken,
  phone,
}: {
  cartToken: string;
  phone: string;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}`,
    method: "PATCH",
    input: { phone },
    outputSchema: cartResponseSchema,
  });
}

const cartItemInputSchema = z.object({
  variant_id: z.number(),
  quantity: z.number(),
});

export async function updateCartItems(
  cartToken: string,
  cartItems: Array<{
    variant_id: number;
    quantity: number;
  }>,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/items`,
    method: "POST",
    input: { items: cartItems },
    inputSchema: z.object({
      items: z.array(cartItemInputSchema),
    }),
    outputSchema: cartResponseSchema,
  });
}

export async function deleteCartItem(cartToken: string, itemId: number) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/items/${itemId}`,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function removeEnrollmentCartItem(
  cartToken: string,
  itemId: string,
  options?: {
    fluidShop?: string;
    fluidSession?: string;
  },
) {
  let endpoint = `/public/v2025-06/commerce/carts/${cartToken}/items/${itemId}`;

  const queryParams = new URLSearchParams();
  if (options?.fluidShop) {
    queryParams.append("fluid_shop", options.fluidShop);
  }
  if (options?.fluidSession) {
    queryParams.append("fluid_session", options.fluidSession);
  }

  if (queryParams.toString()) {
    endpoint += `?${queryParams.toString()}`;
  }

  return fetchApi({
    endpoint,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartLanguage(
  cartToken: string,
  language: { language_iso: string },
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/change_language`,
    method: "PATCH",
    input: language,
    outputSchema: cartResponseSchema,
  });
}

export async function updateCartVolumeRep(
  cartToken: string,
  volumeRepId: number,
  jwt: string,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/volume_rep`,
    method: "PATCH",
    input: {
      volume_rep_id: volumeRepId,
      order_on_behalf_of: true,
    },
    outputSchema: cartResponseSchema,
    fetchOptions: {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function updateCartItemVariant(
  cartToken: string,
  itemId: number,
  variantId: number,
  quantity: number,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/items/${itemId}/variant`,
    method: "PATCH",
    input: { variant_id: variantId, quantity: quantity },
    outputSchema: cartResponseSchema,
  });
}

export async function applyPoints(cartToken: string, pointsAmount: number) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/apply_points`,
    method: "POST",
    inputSchema: z.object({
      points_amount: z.number().int().positive(),
    }),
    input: {
      points_amount: pointsAmount,
    },
    outputSchema: cartResponseSchema,
  });
}

export async function removePoints(cartToken: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/remove_points`,
    method: "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function syncCustomer({
  cartToken,
  jwt,
}: {
  cartToken: string;
  jwt: string;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/sync_customer`,
    method: "POST",
    outputSchema: cartResponseSchema,
    fetchOptions: {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function updateCartMetaData({
  cartToken,
  metaData,
}: {
  cartToken: string;
  metaData: Record<string, unknown>;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/metadata`,
    method: "PATCH",
    input: { metadata: metaData },
    outputSchema: cartResponseSchema,
  });
}
